import { useEffect, useRef, useState } from 'react'
import type { PropsWithChildren, RefObject } from 'react'
import Image from '~/components/image/Image'
import { PAYPAY_IMAGE_URL } from '~/lib/constants/constants'
import { money } from '~/lib/money/money'
import { calcTableCount } from '~/lib/seat/seat'
import { useIsPC } from '~/lib/ua'
import { css, sva } from '~/styled-system/css'
import { Collapsible } from '~/ui/Collapsible'
import { useIsCancelGuardPlan } from '../../card/cancel-guard'
import { useAdultCount, useChildMenuAmount } from '../../store/childMenu'
import { useSelectedOptionMenusWithDetails } from '../../store/optionMenu'
import {
  useAppliedIkyuPoints,
  useAppliedPaypayPoints,
  useDisplayGivenPointsType,
  useGivenPoints,
  useHiddenBreakDown,
  useOffPriceByCoupon,
  usePaymentAmount,
  useRequiresOnsitePayment,
} from '../../store/payment'
import { useSelectedSeat } from '../../store/seat'
import { useSelectedGivenPointsType, useVisitorsCount } from '../../store/stateMachine/context'
import { useOptionMenus, usePlan, useReserveInfo } from '../../store/stateMachine/state'
import { Section } from '../Section'

export function PaymentAmountDetails() {
  const isPC = useIsPC()
  const Details = isPC ? PCPaymentAmountDetails : SDPaymentAmountDetails
  return <Details />
}

const pcPaymentStyles = sva({
  slots: ['container', 'head', 'title', 'content', 'information', 'float'],
  base: {
    container: {
      bg: 'white',
      p: '6',
      spaceY: '3',
    },
    head: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    title: {
      fontSize: '2xl',
      fontWeight: 'bold',
    },
    content: {
      w: '[424px]',
      ml: 'auto',
      spaceY: '3',
      border: 'medium',
      borderRadius: 'default',
      p: '4',
    },
    information: {
      fontSize: 'md',
    },
    float: {
      // 対応する relative が scroll 要素のため、absolute を使うとスクロール時にフロートがついてきてしまうので fixed を使う
      position: 'fixed',
      bottom: '0',
      w: 'content.modal',
      left: '0',
      right: '0',
      marginX: 'auto',
      bg: 'white',
      px: '6',
      py: '5',
      shadow: 'outline.lg',
    },
  },
})

function PCPaymentAmountDetails() {
  const requires = useRequiresOnsitePayment()
  const ref = useRef<HTMLDivElement>(null)
  const isIntersecting = useIsIntersecting(ref)
  const classes = pcPaymentStyles()
  const isCancelGuardPlan = useIsCancelGuardPlan()
  return (
    <div className={classes.container}>
      <div>
        <div className={classes.head}>
          <h2 className={classes.title}>支払料金</h2>
          {requires && <p className={classes.information}>現地にてご精算ください</p>}
        </div>
        {isCancelGuardPlan && (
          <p className={css({ fontSize: 'xs', textAlign: 'right' })}>
            ※キャンセル料発生時のみクレジットカード決済
          </p>
        )}
      </div>
      <div className={classes.content}>
        <div ref={ref}>
          <PaymentAmount />
        </div>
        {isIntersecting || (
          <div className={classes.float}>
            <PaymentAmount />
          </div>
        )}
        <Breakdown />
        <SavingGivenPoints />
      </div>
    </div>
  )
}

function SDPaymentAmountDetails() {
  const requires = useRequiresOnsitePayment()
  const ref = useRef<HTMLDivElement>(null)
  const isIntersecting = useIsIntersecting(ref)
  const isCancelGuardPlan = useIsCancelGuardPlan()
  return (
    <Section
      title="支払料金"
      annotation={
        <div className={css({ textAlign: 'right' })}>
          {requires && <p className={css({ fontSize: 'md' })}>現地にてご精算ください</p>}
          {isCancelGuardPlan && (
            <p className={css({ fontSize: 'xs', textAlign: 'right', mt: '1' })}>
              ※キャンセル料発生時のみクレジットカード決済
            </p>
          )}
        </div>
      }
      headerAlignItems="top"
    >
      <div>
        <div ref={ref}>
          <PaymentAmount />
        </div>
        <Breakdown />
      </div>
      {isIntersecting || (
        // NOTE: この absolute は Fusuma の section.relative に対応している
        <div className="absolute bottom-0 left-0 w-full bg-white px-3 py-2 shadow-outline">
          <PaymentAmount />
        </div>
      )}
      <SavingGivenPoints />
    </Section>
  )
}

function PaymentAmount() {
  const total = usePaymentAmount()
  return (
    <>
      <div
        className={css({ display: 'flex', justifyContent: 'space-between', alignItems: 'center' })}
      >
        <p>合計金額</p>
        <p className={css({ fontSize: '3xl', fontWeight: 'bold' })}>{money(total)}円</p>
      </div>
      <p className={css({ textAlign: 'right', fontSize: 'xs', color: 'gray.700' })}>
        消費税・サービス料込
      </p>
    </>
  )
}

function useIsIntersecting(ref: RefObject<HTMLDivElement>) {
  const isPc = useIsPC()
  const [isIntersecting, setIsIntersecting] = useState(false)
  useEffect(() => {
    if (ref.current === null) {
      return
    }
    // Fusuma で overflow-y が設定されている div 要素 (data-modal または data-fusuma) に対する交差判定をする
    const selector = isPc ? '[data-modal]' : '[data-fusuma]'
    const root = ref.current.closest(selector)
    // -20px, -8px はフロートの PaymentAmount についている pb-5, pb-2 の分
    const rootMargin = isPc ? '1000% 0% -20px' : '1000% 0% -8px'
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting)
      },
      // 上方向に対して判定範囲を広げることで PaymentAmount が viewport の上方向にスクロールしてしまっても交差していることにする
      { root, rootMargin, threshold: 1 }
    )
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [ref])
  return isIntersecting
}

function Breakdown() {
  const hidden = useHiddenBreakDown()
  if (hidden) {
    return null
  }
  return (
    <Collapsible
      header={
        <p className={css({ color: 'link', fontSize: 'sm', _pc: { fontSize: 'base' } })}>内訳</p>
      }
      headerPosition="end"
      triggerClassName={css({ p: '1', cursor: 'pointer' })}
      contentClassName={css({ p: '0' })}
      color="blue"
    >
      <div className="block w-full space-y-2 rounded bg-neutral-100 p-2">
        <PlanAmount />
        <ChildMenusAmount />
        <ChargeAmount />
        <OptionMenusAmount />
        <DiscountCouponAmount />
        <AppliedIkyuPoints />
        <AppliedGivenPoints />
        <AppliedPayPayPoints />
      </div>
    </Collapsible>
  )
}

function PlanAmount() {
  const { isSeatOnly, adultCount, price } = usePlanAmount()
  if (isSeatOnly) {
    return null
  }
  return (
    <BaseLine label="プラン料金">
      <Details count={adultCount} price={price} />
    </BaseLine>
  )
}
function usePlanAmount() {
  const isSeatOnly = usePlan()?.isSeatOnly ?? false
  const adultCount = useAdultCount()
  const price = useReserveInfo()?.inventory.price ?? 0
  return { isSeatOnly, adultCount, price }
}

function ChildMenusAmount() {
  const menus = useChildMenuAmount()
  if (menus.length === 0) {
    return null
  }
  return (
    <BaseLine label="お子様メニュー">
      <div className="flex flex-col items-end gap-1 space-y-2">
        {menus.map((menu) => (
          <Details key={menu.childMenuId} count={menu.count} price={menu.price} />
        ))}
      </div>
    </BaseLine>
  )
}

function ChargeAmount() {
  const { seat, adultCount, visitorsCount } = useChargeAmount()
  if (seat === undefined || seat.charge.__typename === 'SeatChargeNoCharge') {
    return null
  }
  return (
    <BaseLine label="チャージ料金">
      <p className="flex items-center space-x-1 text-sm leading-none">
        <span>{money(seat.charge.price)}円</span>
        <span>×</span>
        {seat.charge.__typename === 'SeatChargePerPerson' && <span>{adultCount}名</span>}
        {seat.charge.__typename === 'SeatChargePerTable' && (
          <span>{calcTableCount(visitorsCount, seat.capacity)}卓</span>
        )}
        <span>=</span>
        <span className="text-lg">{money(seat.chargeAmount)}円</span>
      </p>
    </BaseLine>
  )
}
function useChargeAmount() {
  const seat = useSelectedSeat()
  const adultCount = useAdultCount()
  const visitorsCount = useVisitorsCount()
  return { seat, adultCount, visitorsCount }
}

function OptionMenusAmount() {
  const { menus, selectedMenus } = useOptionMenusAmount()
  // オプションメニューがないプランの場合はオプション料金を表示しない
  if (menus.length === 0) {
    return null
  }
  return (
    <BaseLine label="オプション料金">
      {selectedMenus.length > 0 ? (
        <div className="flex flex-col items-end gap-1 space-y-2">
          {selectedMenus.map((menu) => (
            <Details key={menu.optionMenuId} count={menu.count} price={menu.price} />
          ))}
        </div>
      ) : (
        // オプションメニューが１つも選択されていない場合はオプション料金を0円で表示する
        <p className="text-lg">0円</p>
      )}
    </BaseLine>
  )
}

function useOptionMenusAmount() {
  const menus = useOptionMenus()
  const selectedMenus = useSelectedOptionMenusWithDetails()
  return { menus, selectedMenus }
}

function DiscountCouponAmount() {
  const amount = useOffPriceByCoupon()
  if (amount === 0) {
    return null
  }
  return <DiscountAmount label="割引クーポン" amount={amount} />
}

function AppliedIkyuPoints() {
  const points = useAppliedIkyuPoints()
  if (points === 0) {
    return null
  }
  return <DiscountAmount label="保有ポイント" amount={points} />
}

function AppliedGivenPoints() {
  const { point, displayType } = useGivePointsUsage()
  if (displayType !== 'USE_INSTANTLY') {
    return null
  }
  return <DiscountAmount label="ポイント即時利用" amount={point} />
}
function useGivePointsUsage() {
  const point = useGivenPoints()
  const displayType = useDisplayGivenPointsType()
  return { point, displayType }
}

function AppliedPayPayPoints() {
  const point = useAppliedPaypayPoints()
  if (point === 0) {
    return null
  }
  return <DiscountAmount label="PayPay（残高）等利用" amount={point} />
}

function SavingGivenPoints() {
  const { type, points, displayType } = useGivenPointsSave()
  if (displayType !== 'SAVE') {
    return null
  }
  switch (type) {
    case 'PAYPAY':
      return (
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <p>獲得ポイント</p>
            <div className="text-right">
              <div className="flex items-center justify-end space-x-1 text-md">
                <span className="size-3">
                  <Image src={PAYPAY_IMAGE_URL} width={12} height={12} alt="PayPayイメージ" />
                </span>
                <span className="font-bold">{money(points)}円分</span>
              </div>
            </div>
          </div>
          <p className="text-right text-xs text-neutral-700">
            ※付与されるのはPayPayポイントです。出金と譲渡はできません。
          </p>
        </div>
      )
    case 'IKYU':
      return (
        <div className="flex items-center justify-between">
          <p>獲得ポイント</p>
          <div className="flex items-center justify-end space-x-1 text-right text-md">
            <span className="font-bold">一休ポイント{money(points)}円分</span>
          </div>
        </div>
      )
  }
}
function useGivenPointsSave() {
  const type = useSelectedGivenPointsType()
  const points = useGivenPoints()
  const displayType = useDisplayGivenPointsType()
  return { type, points, displayType }
}

function Details({ count, price }: { count: number; price: number }) {
  return (
    <p className="flex items-center space-x-1 text-sm leading-none">
      <span>{money(price)}円</span>
      <span>×</span>
      <span>{count}点</span>
      <span>=</span>
      <span className="text-lg">{money(price * count)}円</span>
    </p>
  )
}

function DiscountAmount({ label, amount }: { label: string; amount: number }) {
  if (amount === 0) {
    return null
  }
  return (
    <BaseLine label={label}>
      <p className={css({ fontSize: 'lg', color: 'accent.pink' })}>-{money(amount)}円分</p>
    </BaseLine>
  )
}

function BaseLine({ label, children }: PropsWithChildren<{ label: string }>) {
  return (
    <div className={css({ display: 'flex', justifyContent: 'space-between' })}>
      <p>{label}</p>
      {children}
    </div>
  )
}
